import React from "react";
import { useAppContext } from "../../AppProvider";
import { getFieldError } from "../../utilities/utilities";
import { Contract } from "../../Models/Contract";
import SyncSelect from "../../Components/Selects/SyncSelect/SyncSelect";
import { useIndexContractsContext } from "./providers/Index";
import InputBoxIcon from "../../Components/Inputs/InputIcon/InputIcon";
import { faLoader } from "../../utilities/fontawesomeIcons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTypes } from "../../utilities/types";
import ButtonConfirm from "../../Components/Buttons/Confirm";
import ButtonUtility from "../../Components/Buttons/Utility";
import AsyncSelect from "../../Components/Selects/AsyncSelect/AsyncSelect";
import { FetchParam } from "../../Models/FetchParam";
import { CarrierConfigurationOption } from "../../Components/Selects/AsyncSelect/styles";
import { getAllCarriersFromCarrierConfigurations } from "../../Components/Selects/AsyncSelect/api";
import Datepicker from "../../Components/DatePickers/DatePicker/DatePicker";
import { CarrierConfiguration } from "../../Models/CarrierConfiguration";
import Switch from "../../Components/Switches/Switch";
import { useHandlerContext } from "./providers/Handler";

const Handler = () => {
  const { dictionary } = useAppContext();
  const { data, error, loader, callback, edit, onSubmit } = useHandlerContext();
  const indexContractsContext = useIndexContractsContext();
  const modalConfig = indexContractsContext?.modalConfig;
  const { contractTypes, shipments } = useTypes();

  const handleChange = (
    name: string,
    value: string | boolean | number | object | null,
    key: string
  ) => {
    edit(
      {
        ...data,
        [name]: value,
      } as Contract,
      key as keyof Contract,
      false
    );
  };

  const shipmentType = shipments.find((x) => x.value === data.shipmentType);
  const contractType = contractTypes.find((x) => x.value === data.type);

  return (
    <React.Fragment>
      {loader ? (
        <div className="text-center mt-2">
          <FontAwesomeIcon icon={faLoader} spin size="2x" />
        </div>
      ) : (
        <React.Fragment>
          <h3 className="text-center">{modalConfig?.title}</h3>

          <div
            className="mb-2"
            style={{
              display: "grid",

              gridTemplateColumns: "1fr",
              rowGap: "0.5rem",
              columnGap: "1.5rem",
              alignItems: "start",
            }}
          >
            <div>
              <label className="label-2">
                {dictionary.registries.carriers}
              </label>
              <AsyncSelect
                name="carrierConfiguration"
                optionValue="id"
                optionLabel="name"
                value={data.carrierConfiguration ?? null}
                onSearch={(
                  name: string,
                  value: CarrierConfiguration | CarrierConfiguration[] | null
                ) => {
                  handleChange(name, value, "carrierConfiguration");
                }}
                loadOptions={(inputValue, pagination) => {
                  const params: FetchParam = {
                    inputValue,
                    pagination,
                    sorting: { selector: "name", orderBy: "asc" },
                  };
                  return getAllCarriersFromCarrierConfigurations(params);
                }}
                optionStyle={(option: CarrierConfiguration) => (
                  <CarrierConfigurationOption option={option} />
                )}
                isClearable={false}
                error={getFieldError(error, "carrierConfiguration")}
                placeholder={dictionary.actions.select}
              />
            </div>

            <div className="d-flex w-100">
              <div className="w-50 me-2">
                <label className="label-2">{dictionary.registries.name}</label>
                <InputBoxIcon
                  name="name"
                  value={data.name || ""}
                  type="text"
                  onChange={({ target }) => {
                    handleChange(target.name, target.value, "name");
                  }}
                  error={getFieldError(error, "name")}
                />
              </div>

              <div className="w-50 ">
                <label className="label-2">
                  {dictionary.references.external_reference}
                </label>
                <InputBoxIcon
                  name="externalReference"
                  value={data.externalReference || ""}
                  type="text"
                  onChange={({ target }) => {
                    handleChange(
                      target.name,
                      target.value,
                      "externalReference"
                    );
                  }}
                  error={getFieldError(error, "externalReference")}
                />
              </div>
            </div>

            <div className="d-flex w-100">
              <div className="w-50 me-2">
                <label className="label-2">
                  {dictionary.registries.description}
                </label>
                <InputBoxIcon
                  name="description"
                  value={data?.description || ""}
                  type="text"
                  onChange={({ target }) => {
                    handleChange(target.name, target.value, "description");
                  }}
                  error={getFieldError(error, "description")}
                />
              </div>

              <div className="w-50 ">
                <label className="label-2">
                  {dictionary.contracts.contract_typology}
                </label>
                <SyncSelect
                  name="type"
                  value={contractType ?? null}
                  onChange={(name: string, value: any | any[] | null) =>
                    handleChange(name, value.value, "type")
                  }
                  options={contractTypes}
                  isClearable={false}
                  error={getFieldError(error, "type")}
                />
              </div>
            </div>
            <div className="d-flex w-100">
              <div className="w-50 me-2">
                <label className="label-2">
                  {dictionary.times.validity_from}
                </label>
                <Datepicker
                  name="validityFrom"
                  selectedDate={data?.validityFrom || null}
                  onChange={(name: string, value: Date | null) =>
                    handleChange(name, value, "validityFrom")
                  }
                  error={getFieldError(error, "validityFrom")}
                />
              </div>

              <div className="w-50">
                <label className="label-2">
                  {dictionary.times.validity_to}
                </label>
                <Datepicker
                  name="validityTo"
                  selectedDate={data?.validityTo || null}
                  onChange={(name: string, value: Date | null) =>
                    handleChange(name, value, "validityTo")
                  }
                  error={getFieldError(error, "validityTo")}
                />
              </div>
            </div>

            <div className="d-flex w-100">
              <div className="w-50 me-2">
                <label className="label-2">
                  {dictionary.shipments.shipment_typology}
                </label>
                <SyncSelect
                  name="shipmentType"
                  value={shipmentType ?? null}
                  onChange={(name: string, value: any | any[] | null) =>
                    handleChange(name, value.value, "shipmentType")
                  }
                  options={shipments}
                  isClearable={false}
                  error={getFieldError(error, "shipmentType")}
                />
              </div>
              <div className=" w-50 d-flex align-items-end justify-content-end">
                <Switch
                  name="active"
                  checked={data.active ?? false}
                  onChange={(e) => {
                    handleChange(e.target.name, !data.active, "active");
                  }}
                  label={dictionary.words.active}
                />
              </div>
            </div>
          </div>

          <div className="d-flex mt-3">
            <ButtonUtility
              className="w-50 me-2"
              onClick={() => {
                if (callback) {
                  callback();
                }
              }}
              disabled={loader}
            >
              {dictionary.actions.cancel}
            </ButtonUtility>
            <ButtonConfirm
              className="w-50"
              onClick={() => onSubmit(data?.id)}
              disabled={loader}
              loading={loader}
            >
              {dictionary.actions.save}
            </ButtonConfirm>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default Handler;
